import DashboardCard from 'areas/dashboard/components/DashboardCard';
import { PageSection } from 'common/styles/page';
import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { useApiPortalV1AssessmentsResultsListQuery } from 'areas/assessment-schedule/api';
import styled from 'styled-components';
import Select from 'react-select';
import CategorySelect from 'common/components/CategorySelect';
import { AggregateType } from 'common/constants/AggregateType';
import {
  getSelectedAssessmentPeriodCategories,
  getSelectedAssessmentPeriodId,
} from 'common/selectors/auth';

type Confidence = {
  start: number;
  end: number;
};

type Detail = {
  department: string;
  score: number;
  change: string;
};

type RowData = {
  question: string;
  factor: number;
  overall_score: number;
  change: string;
  confidence_interval: Confidence;
  details: Detail[];
};

const AnalyticsQuestions: React.FC = () => {
  const [expandedRows, setExpandedRows] = useState<{ [key: number]: boolean }>({});

  const toggleRow = (index: number) => {
    setExpandedRows(prev => ({ ...prev, [index]: !prev[index] }));
  };

  const assessmentPeriodId = useSelector(getSelectedAssessmentPeriodId);
  const assessmentPeriodCategories = useSelector(
    getSelectedAssessmentPeriodCategories
  ) as any[];

  const { data: data_by_category } = useApiPortalV1AssessmentsResultsListQuery({
    aggregateType: AggregateType.CATEGORY_QUESTION_SCORES,
    assessmentPeriodId: assessmentPeriodId,
  });
  const { data: data_by_pillar } = useApiPortalV1AssessmentsResultsListQuery({
    aggregateType: AggregateType.CATEGORY_PILLARS,
    assessmentPeriodId: assessmentPeriodId,
  });
  const { data: data_by_option } = useApiPortalV1AssessmentsResultsListQuery({
    aggregateType: AggregateType.OPTION_QUESTION_SCORES,
    assessmentPeriodId: assessmentPeriodId,
  });

  type PillarOption = { value: number; label: string };
  const [filterPillars, setFilterPillars] = useState<PillarOption[]>([]);

  const [selectedCategory, setSelectedCategory] = useState<string | null>(null);
  const [selectedPillar, setSelectedPillar] = useState<number | null>(0);
  const [questionData, setQuestionData] = useState<RowData[]>([]);
  const ALL_PILLARS = -1;

  useEffect(() => {
    if (data_by_pillar && data_by_pillar.length > 0) {
      const pillars: PillarOption[] = Array.from(
        new Map<string, PillarOption>(
          data_by_pillar[0]?.result?.pillars.map(
            (score: { pillar_name: string; pillar: number }) => [
              score.pillar,
              { value: score.pillar, label: score.pillar_name },
            ]
          )
        ).values()
      );
      const allPillars = { value: ALL_PILLARS, label: 'All Pillars' };
      const updatedPillars = [allPillars, ...pillars];

      setFilterPillars(updatedPillars);

      const selectedPillarExists = updatedPillars.some(pillar => pillar.value === selectedPillar);
      if (!selectedPillar || !selectedPillarExists) {
        setSelectedPillar(updatedPillars[0]?.value || null);
      }
    }
  }, [data_by_pillar]);

  useEffect(() => {
    if (!assessmentPeriodCategories || assessmentPeriodCategories.length === 0) {
      setSelectedCategory(null);
      return;
    }

    const selectedCategoryExists = assessmentPeriodCategories.some(
      category => category.id === selectedCategory
    );

    if (selectedCategoryExists) return;

    const primaryCategory = assessmentPeriodCategories.find(
      category => category.priority === 'primary'
    );

    setSelectedCategory(primaryCategory?.id || assessmentPeriodCategories[0]?.id || null);
  }, [assessmentPeriodCategories, selectedCategory]);

  useEffect(() => {
    if (
      data_by_category &&
      data_by_category.length > 0 &&
      data_by_option &&
      data_by_option.length > 0
    ) {
      const filteredCategoryData = data_by_category[0]?.result?.filter(
        (score: { factor: number; category_id: string; question: string; overall_score: number }) =>
          (selectedPillar === -1 || score.factor === selectedPillar) &&
          score.category_id === selectedCategory
      );

      const filteredOptionData = data_by_option[0]?.result?.filter(
        (score: { factor: number; category_id: string; question: string; overall_score: number }) =>
          (selectedPillar === -1 || score.factor === selectedPillar) &&
          score.category_id === selectedCategory
      );
      const questions: RowData[] = Array.from(
        new Map<string, RowData>(
          filteredCategoryData?.map(
            (score: {
              question: string;
              overall_score: number;
              factor: number;
              confidence_interval: Confidence;
            }) => [
              score.question,
              {
                question: score.question,
                overall_score: score.overall_score,
                factor: score.factor,
                change: '0',
                confidence_interval: score.confidence_interval,
                details: [],
              },
            ]
          )
        ).values()
      );

      questions.forEach(question => {
        filteredOptionData
          .filter((score: { question: string }) => score.question === question.question)
          .map((score: { option_name: string; overall_score: number }) => {
            question.details.push({
              department: score.option_name,
              score: score.overall_score,
              change: '0',
            });
          });
      });

      questions.sort((a, b) => a.factor - b.factor);
      setQuestionData(questions);
    }
  }, [data_by_category, data_by_option, selectedCategory, selectedPillar, assessmentPeriodId]);

  const getPillarLabel = (value: number): string => {
    const foundPillar = filterPillars.find(pillar => pillar.value === value);
    return foundPillar ? foundPillar.label : 'Label not found';
  };

  return (
    <DashboardCard
      title="Assessment Questions"
      controls={
        <>
              <Select
            options={filterPillars}
            placeholder="Select Pillar"
            value={filterPillars.find(option => option.value === selectedPillar)}
            onChange={option => setSelectedPillar(option ? option.value : null)}
            menuPortalTarget={document.body}
            styles={{
              container: base => ({
                ...base,
                minWidth: '200px',
                maxWidth: '280px',
              }),
              menuPortal: base => ({ ...base, zIndex: 9999 }),
            }}
          />
          <CategorySelect
            categories={assessmentPeriodCategories as any}
            selectedCategory={selectedCategory}
            onCategoryChange={setSelectedCategory}
            placeholder="Select Category"
            width="250px"
          />
        </>
      }
    >
      <PageSection
        padtop="30px"
        display="flex"
        flexDirection="column"
        justifyContent="center"
        style={{ width: '100%', height: '80%' }}
      >
        {questionData.map((row, index) => (
          <div key={index}>
            <Row onClick={() => toggleRow(index)}>
              <QuestionColumn>{row.question}</QuestionColumn>
              <ScoreColumn>{getPillarLabel(row.factor)}</ScoreColumn>
              <ScoreColumn>
                <strong>
                  {row.overall_score.toFixed(1)}
                  {/*  ({row.change}) */}
                </strong>
              </ScoreColumn>
              <BarColumn>
                {index === 0 && (
                  <Legend>
                    {Array.from(Array(11).keys()).map(num => (
                      <span key={num}>{num}</span>
                    ))}
                  </Legend>
                )}
                <Bar>
                  <ConfidenceBar
                    start={(row.confidence_interval.start / 10) * 100}
                    width={
                      ((row.confidence_interval.end - row.confidence_interval.start) / 10) * 100
                    }
                  />
                  <ScoreMarker position={(row.overall_score / 10) * 100} />
                </Bar>
              </BarColumn>
            </Row>
            <SubTable className={expandedRows[index] ? 'expanded' : ''}>
              {row.details.map((detail, idx) => (
                <Row key={idx}>
                  <OptionColumn>{detail.department}</OptionColumn>
                  <BarColumn>
                    <DepartmentBar style={{ width: `${(detail.score / 10) * 100}%` }}>
                      <DepartmentLabel>
                        {detail.score.toFixed(1)} {/*  ({detail.change}) */}
                      </DepartmentLabel>
                    </DepartmentBar>
                  </BarColumn>
                </Row>
              ))}
            </SubTable>
          </div>
        ))}
      </PageSection>
    </DashboardCard>
  );
};

export default AnalyticsQuestions;

const Row = styled.div`
  display: flex;
  align-items: center;
  border-bottom: 1px solid #ccc;
  padding: 10px 0;
  cursor: pointer;
  transition: background 0.3s ease;

  &:hover {
    background: #f9f9f9;
  }
`;

const QuestionColumn = styled.div`
  flex: 4;
  display: flex;
  flex-direction: row;
  justify-content: flex-start;
  padding-right: 20px;
  font-family: 'Poppins';
  font-style: normal;
  font-weight: 500;
  font-size: 12px;
  line-height: 20px;
  max-width: 800px;
  color: #555;
`;

const ScoreColumn = styled.div`
  flex: 1;
  display: flex;
  flex-direction: row;
  justify-content: flex-end;
  padding-right: 20px;
  font-family: 'Poppins';
  font-style: normal;
  font-weight: 500;
  font-size: 12px;
  line-height: 20px;
  max-width: 800px;
  color: #555;
`;

const OptionColumn = styled.div`
  flex: 6;
  display: flex;
  flex-direction: row;
  justify-content: flex-end;
  margin-right: 35px;
  font-family: 'Poppins';
  font-style: normal;
  font-weight: 400;
  font-size: 12px;
  line-height: 20px;
  max-width: 800px;
  color: #777;
`;

const BarColumn = styled.div`
  flex: 8;
  position: relative;
`;

const SubTable = styled.div`
  margin-top: 10px;
  max-height: 0;
  overflow: hidden;
  transition: max-height 1s ease;

  &.expanded {
    max-height: 1000px; /* Large enough to fit the content */
  }
`;

const Bar = styled.div`
  background: #e0e0e0;
  height: 20px;
  position: relative;
  margin: 5px 0;
  border-radius: 5px;
`;

const ConfidenceBar = styled.div<{ start: number; width: number }>`
  position: absolute;
  background: #f88;
  height: 20px;
  left: ${props => props.start}%;
  width: ${props => props.width}%;
  border-radius: 5px;
  transition:
    left 0.5s ease,
    width 0.5s ease;
`;

const ScoreMarker = styled.div<{ position: number }>`
  position: absolute;
  background: #fff;
  border: 1px solid #777;
  border-radius: 50%;
  width: 15px;
  height: 15px;
  top: 50%;
  left: ${props => props.position}%;
  transform: translate(-50%, -50%);
  transition: left 0.5s ease;
`;

const DepartmentBar = styled.div`
  background: #a5cfee;
  height: 20px;
  position: relative;
  margin: 5px 0;
  border-radius: 5px;
  transition:
    left 0.5s ease,
    width 0.5s ease;
`;

const DepartmentLabel = styled.div`
  position: absolute;
  left: calc(100% + 10px);
  font-size: 12px;
  color: #333;
  white-space: nowrap;
`;

const Legend = styled.div`
  display: flex;
  justify-content: space-between;
  font-size: 12px;
  margin-top: -20px;
  margin-bottom: 10px;
  color: #666;
  width: 100%;
  position: relative;
`;

